import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, MapPin, ChevronLeft, ChevronRight, Wifi, Wind, Waves, PawPrint, Calendar, Ship, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from './lib/runtimeConfig';
import { useIsMobile } from './lib/responsive';
import { HotelesSection, useHotelesPublicos } from './v2/HomeHoteles';
import { precioEstancia } from './precioEstancia';

const API = API_BASE;

const fmt = (n) =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(parseFloat(n) || 0);

const imgUrl = (images) => {
    if (!images || images === '' || images === '[]') return null;
    const list = images.replace(/[\[\]"']/g, '').split(',').map(s => s.trim()).filter(Boolean);
    if (!list.length) return null;
    const p = list[0];
    if (p.startsWith('http')) return p;
    const filename = p.replace(/^\/?(?:uploads\/)?/, '');
    return `${API}/uploads/${encodeURIComponent(filename)}`;
};

const allImgUrls = (images) => {
    if (!images || images === '' || images === '[]') return [];
    return images.replace(/[\[\]"']/g, '').split(',').map(s => {
        const p = s.trim();
        if (!p) return null;
        if (p.startsWith('http')) return p;
        const filename = p.replace(/^\/?(?:uploads\/)?/, '');
        return `${API}/uploads/${encodeURIComponent(filename)}`;
    }).filter(Boolean);
};

// ─── CATEGORIES ───────────────────────────────────────────────────────────────
const CATEGORIES = [
    { id: 'all', label: 'Todo', icon: '🏖️' },
    { id: 'apartamento', label: 'Apartamentos', icon: '🏢' },
    { id: 'casa', label: 'Casas', icon: '🏡' },
    { id: 'suite', label: 'Suites', icon: '🌟' },
    { id: 'experiencia', label: 'Experiencias', icon: '⛵' },
];

// ─── HERO SEARCH ──────────────────────────────────────────────────────────────
const FALLBACK_HERO = 'https://rdcfxujipezbgufhhimz.supabase.co/storage/v1/object/public/propiedades/hero-cartagena-default.jpg';

const HeroSearch = ({ onSearch, bgImage, buscando, aviso }) => {
    const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
    const isMobile = useIsMobile();
    const [checkin, setCheckin] = useState('');
    const [checkout, setCheckout] = useState('');
    const [guests, setGuests] = useState(1);
    const [query, setQuery] = useState('');

    const searchBoxResponsive = isMobile
        ? { ...searchBox, flexDirection: 'column', borderRadius: '16px', padding: '14px', gap: '8px' }
        : searchBox;

    const searchDividerResponsive = isMobile
        ? { width: '100%', height: '1px', background: '#e2e8f0', flexShrink: 0 }
        : searchDivider;

    const dynamicHeroStyle = {
        ...heroStyle,
        backgroundImage: bgImage ? `url(${bgImage})` : heroStyle.backgroundImage,
        ...(isMobile ? { height: 'auto', minHeight: '520px', padding: '28px 0' } : {}),
    };

    return (
        <div style={dynamicHeroStyle}>
            <div style={heroOverlay} />
            <div style={heroContent}>
                <h1 style={heroTitle}>Encuentra tu lugar en Cartagena</h1>
                <p style={heroSubtitle}>Propiedades y experiencias exclusivas en el Caribe colombiano</p>

                <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                    <a
                        href="/que-hacer-en-cartagena"
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: '#F2B705', border: '1px solid #F2B705', borderRadius: '999px',
                            padding: '8px 18px', color: '#1A1A2E', textDecoration: 'none',
                            fontSize: '13px', fontWeight: 800,
                        }}
                    >
                        🌴 Qué hacer en Cartagena: guía de planes →
                    </a>
                    <a
                        href="/propietarios.html"
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(255,255,255,0.13)', backdropFilter: 'blur(8px)',
                            border: '1px solid rgba(255,255,255,0.3)', borderRadius: '999px',
                            padding: '8px 18px', color: 'white', textDecoration: 'none',
                            fontSize: '13px', fontWeight: 600, 
                            transition: 'background 0.2s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.13)'}
                    >
                        🏠 ¿Tienes un apartamento? Nosotros lo administramos →
                    </a>
                    <a
                        href="/gerencia-hotelera"
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(255,255,255,0.13)', backdropFilter: 'blur(8px)',
                            border: '1px solid rgba(255,255,255,0.3)', borderRadius: '999px',
                            padding: '8px 18px', color: 'white', textDecoration: 'none',
                            fontSize: '13px', fontWeight: 600, 
                            transition: 'background 0.2s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.13)'}
                    >
                        🏨 ¿Tienes un hotel? Nosotros lo operamos →
                    </a>
                </div>

                <div style={searchBoxResponsive}>
                    <div style={searchField}>
                        <span style={fieldLabel}>DESTINO</span>
                        <input
                            style={searchInput}
                            placeholder="Cartagena, Colombia"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                        />
                    </div>
                    <div style={searchDividerResponsive} />
                    <div style={searchField}>
                        <span style={fieldLabel}>LLEGADA</span>
                        <input style={searchInput} type="date" value={checkin} min={hoy} onChange={e => { setCheckin(e.target.value); if (checkout && checkout <= e.target.value) setCheckout(''); }} />
                    </div>
                    <div style={searchDividerResponsive} />
                    <div style={searchField}>
                        <span style={fieldLabel}>SALIDA</span>
                        <input style={searchInput} type="date" value={checkout} min={checkin || hoy} onChange={e => setCheckout(e.target.value)} />
                    </div>
                    <div style={searchDividerResponsive} />
                    <div style={searchField}>
                        <span style={fieldLabel}>HUÉSPEDES</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button onClick={() => setGuests(g => Math.max(1, g - 1))} style={counterBtn}>-</button>
                            <span style={{ fontWeight: 700, minWidth: '20px', textAlign: 'center' }}>{guests}</span>
                            <button onClick={() => setGuests(g => g + 1)} style={counterBtn}>+</button>
                        </div>
                    </div>
                    <button
                        style={isMobile ? { ...searchBtn, width: '100%', borderRadius: '10px', height: '44px' } : searchBtn}
                        onClick={() => onSearch({ query, checkin, checkout, guests })}
                        disabled={buscando}
                        aria-label="Buscar disponibilidad"
                    >
                        <Search size={20} color="white" />
                        {isMobile && <span style={{ color: 'white', fontWeight: 800, fontSize: '15px', marginLeft: '8px' }}>{buscando ? 'Buscando…' : 'Buscar'}</span>}
                    </button>
                </div>
                {aviso && <div style={searchAviso}>{aviso}</div>}
            </div>
        </div>
    );
};

// ─── PROPERTY CARD ────────────────────────────────────────────────────────────
const PropertyCard = ({ prop, onClick, fechas }) => {
    const precioNoche = fechas ? precioEstancia(prop.basePrice, prop.calendarEvents, fechas.checkin, fechas.checkout, prop.temporadas).promedio : prop.basePrice;
    const [imgIdx, setImgIdx] = useState(0);
    const [liked, setLiked] = useState(false);
    const images = allImgUrls(prop.images);
    const cover = images[imgIdx] || 'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=600&q=80';

    return (
        <div style={cardStyle} onClick={onClick}>
            <div style={cardImgWrap}>
                <img
                    src={cover}
                    alt={prop.title}
                    style={cardImg}
                    onError={e => { e.target.src = 'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=600&q=80'; }}
                />
                <button
                    style={{ ...heartBtn, color: liked ? '#FF385C' : 'white' }}
                    onClick={e => { e.stopPropagation(); setLiked(l => !l); }}
                >
                    <Heart size={20} fill={liked ? '#FF385C' : 'transparent'} />
                </button>
                {images.length > 1 && (
                    <>
                        <button style={{ ...navBtn, left: '8px' }} onClick={e => { e.stopPropagation(); setImgIdx(i => (i - 1 + images.length) % images.length); }}>
                            <ChevronLeft size={16} />
                        </button>
                        <button style={{ ...navBtn, right: '8px' }} onClick={e => { e.stopPropagation(); setImgIdx(i => (i + 1) % images.length); }}>
                            <ChevronRight size={16} />
                        </button>
                        <div style={dotsWrap}>
                            {images.slice(0, 5).map((_, i) => (
                                <div key={i} style={{ ...dot, background: i === imgIdx ? 'white' : 'rgba(255,255,255,0.5)' }} />
                            ))}
                        </div>
                    </>
                )}
                <div style={categoryBadge}>{prop.category}</div>
            </div>
            <div style={cardBody}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                        <div style={cardTitle}>{prop.title}</div>
                        <div style={cardLocation}><MapPin size={12} /> {prop.address}</div>
                        <div style={cardMeta}>
                            <span>{prop.guests} huéspedes</span>
                            <span>·</span>
                            <span>{prop.bedrooms} habitación{prop.bedrooms !== 1 ? 'es' : ''}</span>
                            <span>·</span>
                            <span>{prop.fullBathrooms} baño{prop.fullBathrooms !== 1 ? 's' : ''}</span>
                        </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={cardPrice}>{fmt(precioNoche)}</div>
                        <div style={cardPerNight}>por noche</div>
                        {Number(prop.cleaningFee) > 0 && (
                            <div style={cleaningFeeNote}>+ {fmt(prop.cleaningFee)} limpieza</div>
                        )}
                    </div>
                </div>
                <div style={(prop.bookingMode || 'INSTANT_BOOK') === 'INSTANT_BOOK' ? bookingModeInstant : bookingModeRequest}>
                    {(prop.bookingMode || 'INSTANT_BOOK') === 'INSTANT_BOOK' ? '⚡ Reserva instantánea' : '🕐 Sujeta a aprobación'}
                </div>
                <div style={amenitiesRow}>
                    {prop.hasAC && <span style={amenityBadge}><Wind size={11} /> A/C</span>}
                    {prop.hasPool && <span style={amenityBadge}><Waves size={11} /> Piscina</span>}
                    {prop.isPetFriendly && <span style={amenityBadge}><PawPrint size={11} /> Mascotas</span>}
                    {prop.wifiName && <span style={amenityBadge}><Wifi size={11} /> WiFi</span>}
                </div>
            </div>
        </div>
    );
};

// ─── EXPERIENCE CARD ──────────────────────────────────────────────────────────
const ExperienceCard = ({ exp, onClick }) => {
    const [liked, setLiked] = useState(false);
    const cover = imgUrl(exp.images) || 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=600&q=80';

    return (
        <div style={cardStyle} onClick={onClick}>
            <div style={cardImgWrap}>
                <img src={cover} alt={exp.title} style={cardImg}
                    onError={e => { e.target.src = 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=600&q=80'; }} />
                <button
                    style={{ ...heartBtn, color: liked ? '#FF385C' : 'white' }}
                    onClick={e => { e.stopPropagation(); setLiked(l => !l); }}
                >
                    <Heart size={20} fill={liked ? '#FF385C' : 'transparent'} />
                </button>
                <div style={{ ...categoryBadge, background: '#FF385C' }}>Experiencia</div>
            </div>
            <div style={cardBody}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                        <div style={cardTitle}>{exp.title}</div>
                        <div style={cardLocation}><MapPin size={12} /> {exp.location}</div>
                        <div style={cardMeta}>
                            <span><Calendar size={11} /> {exp.duration}</span>
                            <span>·</span>
                            <span>{exp.category}</span>
                        </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={cardPrice}>{fmt(exp.price)}</div>
                        <div style={cardPerNight}>por persona</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── MAIN HOME VIEW ───────────────────────────────────────────────────────────
const HomeView = ({ properties = [], experiences = [], auth, settings = {} }) => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [activeCategory, setActiveCategory] = useState('all');
    const [activeExpCategory, setActiveExpCategory] = useState('all');
    const [searchFilter, setSearchFilter] = useState(null);
    const [availableIds, setAvailableIds] = useState(null); // null = sin filtro, [] = ninguna
    const [checkingAvail, setCheckingAvail] = useState(false);
    const [availMsg, setAvailMsg] = useState('');
    const [heroBg, setHeroBg] = useState('');
    const [avisoBusqueda, setAvisoBusqueda] = useState('');
    const resultadosRef = useRef(null);
    // Vyara 2.0: hoteles publicados, como un apartado más junto a alojamientos y experiencias
    const hotelesPublicos = useHotelesPublicos();
    const hayHoteles = (hotelesPublicos || []).length > 0;
    const [hotelesVisibles, setHotelesVisibles] = useState(0);
    const categorias = hayHoteles ? [...CATEGORIES.slice(0, 4), { id: 'hotel', label: 'Hoteles', icon: '🏨' }, ...CATEGORIES.slice(4)] : CATEGORIES;

    useEffect(() => {
        fetch(`${API}/api/settings/public-content`)
            .then(r => r.json())
            .then(d => { if (d.heroBannerBg) setHeroBg(d.heroBannerBg); })
            .catch(() => {});
    }, []);

    // Cuando el usuario busca con fechas, consultar disponibilidad
    const irAResultados = () => setTimeout(() => resultadosRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);

    const handleSearch = async (filter) => {
        setAvailMsg('');
        setAvisoBusqueda('');
        const conFechas = !!(filter?.checkin || filter?.checkout);
        if (conFechas && !(filter.checkin && filter.checkout)) {
            setAvisoBusqueda(filter.checkin ? 'Elige también la fecha de salida.' : 'Elige también la fecha de llegada.');
            return;
        }
        if (conFechas && filter.checkout <= filter.checkin) {
            setAvisoBusqueda('La salida debe ser posterior a la llegada.');
            return;
        }
        setSearchFilter(filter);
        setAvailableIds(null);
        if (!conFechas) { irAResultados(); return; } // Sin fechas = mostrar todo
        setCheckingAvail(true);
        irAResultados();
        try {
            const r = await fetch(`${API}/api/properties/availability?checkIn=${filter.checkin}&checkOut=${filter.checkout}&guests=${filter.guests}`);
            const d = await r.json().catch(() => ({}));
            if (!r.ok || !Array.isArray(d.properties)) throw new Error(d.error || '');
            const ids = d.properties.map(p => p.id);
            setAvailableIds(ids);
            if (ids.length === 0) setAvailMsg(`No hay propiedades disponibles para las fechas ${filter.checkin} → ${filter.checkout} con ${filter.guests} persona(s).`);
        } catch (e) {
            setAvailMsg(e.message === 'Fechas inválidas'
                ? 'Revisa las fechas de llegada y salida e intenta de nuevo.'
                : 'No pudimos consultar la disponibilidad en este momento. Intenta de nuevo.');
        } finally { setCheckingAvail(false); }
    };

    const fechasBuscadas = availableIds !== null && searchFilter?.checkin ? searchFilter : null;

    const filteredProps = useMemo(() => {
        let list = properties;
        if (activeCategory === 'experiencia' || activeCategory === 'hotel') return [];
        if (activeCategory !== 'all') {
            list = list.filter(p => p.category?.toLowerCase().includes(activeCategory));
        }
        if (searchFilter?.query) {
            const q = searchFilter.query.toLowerCase();
            list = list.filter(p => p.title?.toLowerCase().includes(q) || p.address?.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q));
        }
        if (searchFilter?.guests) {
            list = list.filter(p => (p.guests || 1) >= parseInt(searchFilter.guests));
        }
        // Filtrar por disponibilidad de fechas si se buscó con fechas
        if (availableIds !== null) {
            list = list.filter(p => availableIds.includes(p.id));
        }
        return list;
    }, [properties, activeCategory, searchFilter, availableIds]);

    const expCategories = useMemo(() => (
        [...new Set(experiences.map(e => e.category).filter(Boolean))].sort()
    ), [experiences]);

    const filteredExps = useMemo(() => {
        if (activeCategory !== 'all' && activeCategory !== 'experiencia') return [];
        let list = experiences;
        if (activeExpCategory !== 'all') {
            list = list.filter(e => e.category === activeExpCategory);
        }
        if (searchFilter?.query) {
            const q = searchFilter.query.toLowerCase();
            list = list.filter(e => e.title?.toLowerCase().includes(q) || e.location?.toLowerCase().includes(q) || e.category?.toLowerCase().includes(q));
        }
        return list;
    }, [experiences, activeCategory, activeExpCategory, searchFilter]);

    const showExps = activeCategory === 'all' || activeCategory === 'experiencia';
    const showProps = activeCategory !== 'experiencia' && activeCategory !== 'hotel';
    const showHoteles = hayHoteles && (activeCategory === 'all' || activeCategory === 'hotel');

    const gridStyleResponsive = {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: isMobile ? '16px' : '24px',
    };
    const pageBodyResponsive = { ...pageBody, padding: isMobile ? '16px 14px' : '30px 20px' };

    return (
        <div style={{ minHeight: '100vh', background: '#fff' }}>
            <HeroSearch onSearch={handleSearch} bgImage={heroBg} buscando={checkingAvail} aviso={avisoBusqueda} />

            {/* Category filter strip */}
            {/* Banner propietarios — slim strip */}
            <a
                href="/propietarios.html"
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '8px', background: '#003366', padding: '10px 20px',
                    textDecoration: 'none', flexWrap: 'wrap',
                }}
            >
                <span style={{ fontSize: '14px' }}>🏠</span>
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>¿Tienes un apartamento o casa vacacional?</span>
                <span style={{ color: 'white', fontWeight: 700, fontSize: '13px' }}>Nosotros lo administramos.</span>
                <span style={{ color: '#C8A87E', fontWeight: 700, fontSize: '13px', textDecoration: 'underline' }}>Conoce más →</span>
            </a>
            <a
                href="/gerencia-hotelera"
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '8px', background: '#002a54', padding: '10px 20px',
                    textDecoration: 'none', flexWrap: 'wrap', borderTop: '1px solid rgba(255,255,255,0.12)',
                }}
            >
                <span style={{ fontSize: '14px' }}>🏨</span>
                <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>¿Tienes un hotel?</span>
                <span style={{ color: 'white', fontWeight: 700, fontSize: '13px' }}>Nosotros lo operamos.</span>
                <span style={{ color: '#C8A87E', fontWeight: 700, fontSize: '13px', textDecoration: 'underline' }}>Conoce más →</span>
            </a>

            <div style={categoryStrip}>
                <div style={categoryInner}>
                    {categorias.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                            style={{ ...categoryBtn, ...(activeCategory === cat.id ? activeCategoryBtn : {}) }}
                        >
                            <span style={{ fontSize: '20px' }}>{cat.icon}</span>
                            <span>{cat.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div ref={resultadosRef} style={{ ...pageBodyResponsive, scrollMarginTop: '150px' }}>
                {/* Mensaje disponibilidad */}
                {checkingAvail && (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#003366', fontWeight: 700, fontSize: 15 }}>
                        🔍 Verificando disponibilidad para las fechas seleccionadas...
                    </div>
                )}
                {!checkingAvail && availMsg && (
                    <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 14, padding: '18px 24px', margin: '0 0 24px', color: '#854d0e', fontWeight: 700, fontSize: 14 }}>
                        📅 {availMsg}
                    </div>
                )}
                {!checkingAvail && fechasBuscadas && filteredProps.length > 0 && (
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 14, padding: '14px 20px', marginBottom: 20, color: '#166534', fontSize: 13, fontWeight: 600 }}>
                        ✅ {filteredProps.length} propiedad{filteredProps.length !== 1 ? 'es' : ''} disponible{filteredProps.length !== 1 ? 's' : ''} para {searchFilter.checkin} → {searchFilter.checkout}
                    </div>
                )}

                {/* Properties */}
                {showProps && !checkingAvail && filteredProps.length > 0 && (
                    <>
                        <h2 style={sectionTitle}>
                            {fechasBuscadas ? 'Propiedades disponibles' : activeCategory === 'all' ? 'Alojamientos' : 'Propiedades encontradas'}
                            <span style={countBadge}>{filteredProps.length}</span>
                        </h2>
                        <div style={gridStyleResponsive}>
                            {filteredProps.map(prop => (
                                <PropertyCard key={prop.id} prop={prop} fechas={fechasBuscadas} onClick={() => navigate(`/propiedad/${prop.slug}`)} />
                            ))}
                        </div>
                    </>
                )}

                {/* Hoteles */}
                {showHoteles && (
                    <div style={{ marginTop: showProps && filteredProps.length > 0 ? '48px' : '0' }}>
                        <HotelesSection query={searchFilter?.query || ''} estiloTitulo={sectionTitle} onCount={setHotelesVisibles} />
                    </div>
                )}

                {/* Guía de planes */}
                <a href="/que-hacer-en-cartagena" className="guia-banner" style={guiaBanner}>
                    <span style={{ fontSize: '34px', lineHeight: 1 }}>🌴</span>
                    <span style={{ flex: '1 1 260px', minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: '11px', fontWeight: 800, letterSpacing: '1.2px', textTransform: 'uppercase', color: '#F2B705' }}>Guía local</span>
                        <span style={{ display: 'block', fontSize: '22px', fontWeight: 900, color: 'white', lineHeight: 1.2, margin: '2px 0 4px' }}>¿Qué hacer en Cartagena?</span>
                        <span style={{ display: 'block', fontSize: '14px', color: 'rgba(255,255,255,0.85)' }}>Los planes que recomendamos los que vivimos aquí: islas, atardeceres, dónde comer, mapa e itinerarios por días.</span>
                    </span>
                    <span style={{ background: '#F2B705', color: '#1A1A2E', fontWeight: 800, fontSize: '14px', padding: '12px 20px', borderRadius: '999px', whiteSpace: 'nowrap' }}>Ver los planes →</span>
                </a>

                {/* Experiences */}
                {showExps && experiences.length > 0 && (
                    <>
                        <h2 style={{ ...sectionTitle, marginTop: (showProps && filteredProps.length > 0) || (showHoteles && hotelesVisibles > 0) ? '48px' : '0' }}>
                            <Ship size={20} style={{ verticalAlign: 'middle' }} /> Experiencias
                            <span style={countBadge}>{filteredExps.length}</span>
                        </h2>
                        {expCategories.length > 1 && (
                            <div style={expCategoryStrip}>
                                <button
                                    onClick={() => setActiveExpCategory('all')}
                                    style={{ ...expCategoryBtn, ...(activeExpCategory === 'all' ? activeExpCategoryBtn : {}) }}
                                >Todas</button>
                                {expCategories.map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => setActiveExpCategory(cat)}
                                        style={{ ...expCategoryBtn, ...(activeExpCategory === cat ? activeExpCategoryBtn : {}) }}
                                    >{cat}</button>
                                ))}
                            </div>
                        )}
                        {filteredExps.length > 0 ? (
                            <div style={gridStyleResponsive}>
                                {filteredExps.map(exp => (
                                    <ExperienceCard key={exp.id} exp={exp} onClick={() => navigate(`/experiencia/${exp.slug}`)} />
                                ))}
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>No hay experiencias en esta categoría.</div>
                        )}
                    </>
                )}

                {!checkingAvail && filteredProps.length === 0 && filteredExps.length === 0 && !(showHoteles && hotelesVisibles > 0) && (
                    <div style={{ textAlign: 'center', padding: '80px 20px', color: '#94a3b8' }}>
                        <Search size={48} style={{ marginBottom: '16px', opacity: 0.4 }} />
                        <h3 style={{ fontWeight: 700, color: '#475569' }}>No se encontraron resultados</h3>
                        <p>Intenta con otro filtro o categoría</p>
                        <button onClick={() => { setSearchFilter(null); setAvailableIds(null); setAvailMsg(''); setActiveCategory('all'); setActiveExpCategory('all'); }} style={{ marginTop: '16px', padding: '10px 20px', background: '#FF385C', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700 }}>Ver todo</button>
                    </div>
                )}

                {/* Footer strip */}
                <div style={footerStrip}>
                    <nav aria-label="Explorar por zona y destino" style={footerLinks}>
                        {ENLACES_EXPLORAR.map(([href, texto]) => <a key={href} href={href} style={footerLink}>{texto}</a>)}
                    </nav>
                    <div style={{ fontWeight: 700, color: '#003366' }}>{settings.businessName || 'Mi Empresa'}</div>
                    <div style={{ color: '#94a3b8', fontSize: '12px' }}>Cartagena, Colombia · {settings.telefono && `Tel: ${settings.telefono}`}</div>
                </div>
            </div>
        </div>
    );
};

// ─── STYLES ───────────────────────────────────────────────────────────────────

const heroStyle = {
    position: 'relative', height: '520px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    // Imagen de respaldo: degradado marino de Cartagena (no depende de servicios externos)
    background: 'linear-gradient(135deg, #001a3a 0%, #003366 50%, #0a5499 100%)',
    backgroundSize: 'cover', backgroundPosition: 'center'
};
const heroOverlay = { position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,20,60,0.55), rgba(0,20,60,0.35))' };
const heroContent = { position: 'relative', zIndex: 1, textAlign: 'center', padding: '0 20px', width: '100%', maxWidth: '860px' };
const heroTitle = { color: 'white', fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 900, margin: '0 0 10px', textShadow: '0 2px 8px rgba(0,0,0,0.3)' };
const heroSubtitle = { color: 'rgba(255,255,255,0.9)', fontSize: '16px', margin: '0 0 30px' };

const searchBox = {
    background: 'white', borderRadius: '60px', display: 'flex', alignItems: 'center',
    boxShadow: '0 8px 30px rgba(0,0,0,0.25)', padding: '6px 6px 6px 24px', gap: '0'
};
const searchField = { flex: 1, display: 'flex', flexDirection: 'column', padding: '8px 16px 8px 0', minWidth: 0 };
const fieldLabel = { fontSize: '10px', fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' };
const searchInput = { border: 'none', outline: 'none', fontSize: '14px', color: '#1e293b', background: 'transparent', padding: '2px 0', width: '100%' };
const searchDivider = { width: '1px', height: '40px', background: '#e2e8f0', flexShrink: 0 };
const searchBtn = {
    width: '48px', height: '48px', borderRadius: '50%', background: '#FF385C',
    border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
};
const searchAviso = { marginTop: '12px', display: 'inline-block', background: '#fef9c3', color: '#854d0e', border: '1px solid #fde047', borderRadius: '10px', padding: '8px 16px', fontSize: '13px', fontWeight: 700 };
const counterBtn = {
    width: '26px', height: '26px', borderRadius: '50%', border: '1px solid #cbd5e1',
    background: 'white', cursor: 'pointer', fontWeight: 700, fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center'
};

const categoryStrip = { borderBottom: '1px solid #f1f5f9', background: 'white', position: 'sticky', top: '60px', zIndex: 100, overflowX: 'auto', WebkitOverflowScrolling: 'touch' };
const categoryInner = { display: 'flex', gap: '4px', padding: '0 16px', width: 'max-content', margin: '0 auto' };
const categoryBtn = {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
    padding: '14px 18px', border: 'none', background: 'transparent', cursor: 'pointer',
    fontSize: '12px', fontWeight: 600, color: '#64748b', borderBottom: '2px solid transparent', whiteSpace: 'nowrap', flexShrink: 0
};
const activeCategoryBtn = { color: '#1e293b', borderBottomColor: '#1e293b' };

const expCategoryStrip = { display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '-8px 0 20px' };
const expCategoryBtn = {
    padding: '8px 16px', borderRadius: '20px', border: '1px solid #e2e8f0', background: 'white',
    cursor: 'pointer', fontSize: '13px', fontWeight: 700, color: '#475569'
};
const activeExpCategoryBtn = { background: '#003366', borderColor: '#003366', color: 'white' };

const pageBody = { maxWidth: '1200px', margin: '0 auto', padding: '30px 20px' };
const sectionTitle = { fontSize: '20px', fontWeight: 900, color: '#1e293b', margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: '10px' };
const countBadge = { fontSize: '12px', fontWeight: 700, background: '#f1f5f9', color: '#475569', padding: '2px 10px', borderRadius: '20px' };
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '24px' };

const cardStyle = { borderRadius: '16px', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' };
const cardImgWrap = { position: 'relative', height: '220px' };
const cardImg = { width: '100%', height: '100%', objectFit: 'cover' };
const heartBtn = { position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', cursor: 'pointer', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))' };
const navBtn = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%',
    width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
};
const dotsWrap = { position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '4px' };
const dot = { width: '5px', height: '5px', borderRadius: '50%' };
const categoryBadge = {
    position: 'absolute', bottom: '10px', left: '10px',
    background: '#003366', color: 'white', fontSize: '10px', fontWeight: 700,
    padding: '3px 8px', borderRadius: '20px', textTransform: 'uppercase'
};
const cardBody = { padding: '12px 0', background: 'white' };
const cardTitle = { fontSize: '14px', fontWeight: 700, color: '#1e293b', marginBottom: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const cardLocation = { fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '4px' };
const cardMeta = { fontSize: '12px', color: '#64748b', display: 'flex', gap: '5px', marginBottom: '8px' };
const cardPrice = { fontSize: '15px', fontWeight: 800, color: '#1e293b' };
const cardPerNight = { fontSize: '11px', color: '#94a3b8' };
const cleaningFeeNote = { fontSize: '10px', color: '#94a3b8', marginTop: '2px' };
const bookingModeBadge = { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 700, padding: '3px 9px', borderRadius: '20px', marginBottom: '8px' };
const bookingModeInstant = { ...bookingModeBadge, background: '#dcfce7', color: '#15803d' };
const bookingModeRequest = { ...bookingModeBadge, background: '#fef3c7', color: '#b45309' };
const amenitiesRow = { display: 'flex', gap: '6px', flexWrap: 'wrap' };
const amenityBadge = { fontSize: '10px', padding: '2px 7px', background: '#f1f5f9', borderRadius: '20px', color: '#475569', display: 'flex', alignItems: 'center', gap: '3px' };

// Páginas de aterrizaje del servidor (src/lib/seoLanding.js): enlace normal, no del router de React
const ENLACES_EXPLORAR = [
    ['/que-hacer-en-cartagena', 'Qué hacer en Cartagena: guía local'],
    ['/apartamentos/bocagrande', 'Apartamentos en Bocagrande'],
    ['/apartamentos/crespo', 'Apartamentos en Crespo'],
    ['/apartamentos/zona-norte', 'Apartamentos en la Zona Norte'],
    ['/apartamentos/cerca-centro-historico', 'Cerca del Centro Histórico'],
    ['/apartamentos/para-grupos', 'Apartamentos para grupos'],
    ['/tours/islas-del-rosario', 'Tour Islas del Rosario'],
    ['/tours/tierra-bomba', 'Pasadía en Tierra Bomba'],
    ['/tours/baru-playa-tranquila', 'Barú y Playa Tranquila'],
    ['/tours/atardecer', 'Atardecer en Cartagena'],
    ['/tours/city-tour', 'City tour'],
];
const guiaBanner = { display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap', margin: '44px 0 0', padding: '22px 26px', borderRadius: '20px', textDecoration: 'none', background: 'linear-gradient(120deg, #0B3954 0%, #003366 55%, #0E9AA7 130%)', boxShadow: '0 10px 30px -14px rgba(11,57,84,0.55)' };
const footerLinks = { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px 18px', marginBottom: '18px' };
const footerLink = { color: '#475569', fontSize: '13px', textDecoration: 'none' };
const footerStrip = { marginTop: '60px', padding: '20px 0', borderTop: '1px solid #f1f5f9', textAlign: 'center' };

export default HomeView;
